import { useState } from 'react';
import {
  VariableIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import type { EmailTemplateVariable } from '@/types/emailTemplate';
import { VARIABLE_TYPE_LABELS } from '@/types/emailTemplate';

interface VariableSidebarProps {
  variables: EmailTemplateVariable[];
  onInsertVariable: (variableKey: string) => void;
  className?: string;
}

export default function VariableSidebar({
  variables,
  onInsertVariable,
  className,
}: VariableSidebarProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleCopy = async (variableKey: string) => {
    try {
      await navigator.clipboard.writeText(`{{${variableKey}}}`);
      setCopiedKey(variableKey);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleInsert = (variableKey: string) => {
    onInsertVariable(variableKey);
  };

  if (variables.length === 0) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <VariableIcon className="w-5 h-5" />
          <span className="text-sm">No variables defined for this template</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <VariableIcon className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-900">Variables</span>
          <span className="text-sm text-gray-500">({variables.length})</span>
        </div>
        {isCollapsed ? (
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Variable List */}
      {!isCollapsed && (
        <div className="border-t border-gray-200">
          <div className="p-3 space-y-2">
            <p className="text-xs text-gray-500 mb-3">
              Click a variable to insert it at the cursor position, or copy the code.
            </p>

            {variables.map((variable) => (
              <div
                key={variable.id}
                className="bg-white rounded-lg border border-gray-200 p-3 hover:border-brand transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-brand bg-brand/10 px-2 py-0.5 rounded">
                        {`{{${variable.variable_key}}}`}
                      </code>
                      {variable.is_required && (
                        <span className="text-xs text-red-500">Required</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {variable.variable_name}
                    </p>
                    {variable.description && (
                      <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                        {variable.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                      <span>{VARIABLE_TYPE_LABELS[variable.data_type]}</span>
                      {variable.example_value && (
                        <>
                          <span>|</span>
                          <span>
                            Example: <span className="text-gray-600">{variable.example_value}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopy(variable.variable_key)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="Copy variable code"
                    >
                      {copiedKey === variable.variable_key ? (
                        <CheckIcon className="w-4 h-4 text-green-500" />
                      ) : (
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInsert(variable.variable_key)}
                      className="px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded transition-colors"
                    >
                      Insert
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
